import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import {
  FileText, ShieldCheck, Certificate, AirplaneTilt, Stack,
  SignOut, GearSix, Bank
} from '@phosphor-icons/react';
import { format } from 'date-fns';
import { useAuth } from '../contexts/AuthContext';
import { ContractTab } from '../components/dashboard/ContractTab';
import { VerificationTab } from '../components/dashboard/VerificationTab';
import { EmploymentTab } from '../components/dashboard/EmploymentTab';
import { InvitationVisaTab } from '../components/dashboard/InvitationVisaTab';
import { AllTab } from '../components/dashboard/AllTab';
import { StatementTab } from '../components/dashboard/StatementTab';

const API = process.env.REACT_APP_BACKEND_URL;

const Dashboard = () => {
  const navigate = useNavigate();
  const { user, logout: authLogout } = useAuth();

  // Contract state
  const [contractTemplates, setContractTemplates] = useState([]);
  const [selectedContractTemplateId, setSelectedContractTemplateId] = useState('');
  const [contractForm, setContractForm] = useState({ fullName: '', passportId: '', address: '', documentDate: new Date() });
  const [contracts, setContracts] = useState([]);
  const [isGenerating, setIsGenerating] = useState(false);
  const [loadingContracts, setLoadingContracts] = useState(true);

  // Verification state
  const [verificationTemplates, setVerificationTemplates] = useState([]);
  const [selectedVerificationTemplateId, setSelectedVerificationTemplateId] = useState('');
  const [verificationForm, setVerificationForm] = useState({ fullName: '', dateOfBirth: '', passportId: '', nationality: '', documentDate: new Date() });
  const [verifications, setVerifications] = useState([]);
  const [isGeneratingVerification, setIsGeneratingVerification] = useState(false);
  const [loadingVerifications, setLoadingVerifications] = useState(true);

  // Employment state
  const [employmentTemplates, setEmploymentTemplates] = useState([]);
  const [selectedEmploymentTemplateId, setSelectedEmploymentTemplateId] = useState('');
  const [employmentForm, setEmploymentForm] = useState({ fullName: '', passportNumber: '', dateOfBirth: '', country: '', documentDate: new Date() });
  const [employmentContracts, setEmploymentContracts] = useState([]);
  const [isGeneratingEmployment, setIsGeneratingEmployment] = useState(false);
  const [loadingEmployment, setLoadingEmployment] = useState(true);

  // Invitation/Visa state
  const [invitationTemplates, setInvitationTemplates] = useState([]);
  const [selectedInvitationTemplateId, setSelectedInvitationTemplateId] = useState('');
  const [visaTemplates, setVisaTemplates] = useState([]);
  const [selectedVisaTemplateId, setSelectedVisaTemplateId] = useState('');
  const [invitationForm, setInvitationForm] = useState({
    sponsorName: '', certificateNumber: '', documentDate: new Date(), arrivalDate: new Date(),
    workEndDate: new Date(), familyName: '', givenName: '', nationality: '', placeOfBirth: '',
    dateOfBirth: '', gender: 'Male', passportNumber: '', passportIssueDate: '', passportExpiryDate: '',
    passportPlaceOfIssue: '', employeeNumber: '', jobTitle: '', salary: '', visaType: 'Tier 5 (General worker)',
    workAddress: '', workCity: '', workCounty: '', workPostcode: '',
    homeAddress: '-', homeCity: '-', homePostcode: '-', homeCountry: '', contactEmail: ''
  });
  const [invitations, setInvitations] = useState([]);
  const [visas, setVisas] = useState([]);
  const [isGeneratingInvitation, setIsGeneratingInvitation] = useState(false);
  const [loadingInvitations, setLoadingInvitations] = useState(true);

  // All tab state
  const [allForm, setAllForm] = useState({
    fullName: '', dateOfBirth: '', passportId: '', nationality: '',
    verificationTemplateId: '', contractTemplateId: '', employmentTemplateId: '',
    verificationDate: new Date(), contractDate: new Date(), employmentDate: new Date()
  });
  const [isGeneratingAll, setIsGeneratingAll] = useState(false);

  // Statement state
  const [statementTemplates, setStatementTemplates] = useState([]);
  const [selectedStatementTemplateId, setSelectedStatementTemplateId] = useState('');
  const [statementForm, setStatementForm] = useState({
    surname: '', sortCode: '', accountNumber: '', openingBalance: '0',
    documentDate: new Date(), addressLastUpdated: new Date(),
    statementFrom: new Date(), statementTo: new Date(),
    transactions: []
  });
  const [statements, setStatements] = useState([]);
  const [isGeneratingStatement, setIsGeneratingStatement] = useState(false);
  const [loadingStatements, setLoadingStatements] = useState(true);

  const getHeaders = () => {
    const token = localStorage.getItem('token');
    return { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) };
  };

  // Load all data on mount
  useEffect(() => {
    if (!user) return;
    const loadTemplates = async () => {
      try {
        const [ct, vt, et, it, vst, stt] = await Promise.all([
          fetch(`${API}/api/templates`, { headers: getHeaders() }).then(r => r.json()),
          fetch(`${API}/api/verification-templates`, { headers: getHeaders() }).then(r => r.json()),
          fetch(`${API}/api/employment-templates`, { headers: getHeaders() }).then(r => r.json()),
          fetch(`${API}/api/invitation-templates`, { headers: getHeaders() }).then(r => r.json()),
          fetch(`${API}/api/visa-templates`, { headers: getHeaders() }).then(r => r.json()),
          fetch(`${API}/api/statement-templates`, { headers: getHeaders() }).then(r => r.json()),
        ]);
        setContractTemplates(ct); if (ct.length) setSelectedContractTemplateId(ct[0].id);
        setVerificationTemplates(vt); if (vt.length) setSelectedVerificationTemplateId(vt[0].id);
        setEmploymentTemplates(et); if (et.length) setSelectedEmploymentTemplateId(et[0].id);
        setInvitationTemplates(it); if (it.length) setSelectedInvitationTemplateId(it[0].id);
        setVisaTemplates(vst); if (vst.length) setSelectedVisaTemplateId(vst[0].id);
        setStatementTemplates(stt); if (stt.length) setSelectedStatementTemplateId(stt[0].id);
        if (vt.length) setAllForm(p => ({...p, verificationTemplateId: vt[0].id}));
        if (ct.length) setAllForm(p => ({...p, contractTemplateId: ct[0].id}));
        if (et.length) setAllForm(p => ({...p, employmentTemplateId: et[0].id}));
      } catch (e) { console.error('Failed to load templates:', e); }
    };
    const loadDocs = async () => {
      try {
        const [c, v, e, inv, vis, stm] = await Promise.all([
          fetch(`${API}/api/contracts`, { headers: getHeaders() }).then(r => r.json()),
          fetch(`${API}/api/verifications`, { headers: getHeaders() }).then(r => r.json()),
          fetch(`${API}/api/employment-contracts`, { headers: getHeaders() }).then(r => r.json()),
          fetch(`${API}/api/invitations`, { headers: getHeaders() }).then(r => r.json()),
          fetch(`${API}/api/visas`, { headers: getHeaders() }).then(r => r.json()),
          fetch(`${API}/api/statements`, { headers: getHeaders() }).then(r => r.json()),
        ]);
        setContracts(c); setVerifications(v); setEmploymentContracts(e); setInvitations(inv); setVisas(vis); setStatements(stm);
      } catch (e) { console.error('Failed to load documents:', e); }
      setLoadingContracts(false); setLoadingVerifications(false); setLoadingEmployment(false); setLoadingInvitations(false); setLoadingStatements(false);
    };
    loadTemplates();
    loadDocs();
  }, [user]);

  const logout = () => { authLogout(); navigate('/login'); };

  // ==================== Handlers ====================
  const handleDownload = async (url, filename) => {
    try {
      const res = await fetch(`${API}${url}`, { headers: getHeaders() });
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      const a = document.createElement('a'); a.href = URL.createObjectURL(blob); a.download = filename; a.click(); URL.revokeObjectURL(a.href);
    } catch { toast.error('Ошибка скачивания'); }
  };

  const handleDelete = async (url, id, listSetter) => {
    try {
      const res = await fetch(`${API}${url}`, { method: 'DELETE', headers: getHeaders() });
      if (res.ok) { listSetter(prev => prev.filter(i => i.id !== id)); toast.success('Удалено'); }
    } catch { toast.error('Ошибка удаления'); }
  };

  const generateContract = async () => {
    setIsGenerating(true);
    try {
      const body = {
        template_id: selectedContractTemplateId, full_name: contractForm.fullName,
        passport_id: contractForm.passportId, address: contractForm.address,
        document_date: format(contractForm.documentDate, 'dd.MM.yyyy')
      };
      const res = await fetch(`${API}/api/contracts/generate`, { method: 'POST', headers: getHeaders(), body: JSON.stringify(body) });
      if (!res.ok) throw new Error((await res.json()).detail || 'Error');
      const data = await res.json();
      setContracts(prev => [data, ...prev]);
      toast.success('Контракт создан');
    } catch (e) { toast.error(e.message); }
    setIsGenerating(false);
  };

  const generateVerification = async () => {
    setIsGeneratingVerification(true);
    try {
      const body = {
        template_id: selectedVerificationTemplateId, full_name: verificationForm.fullName,
        date_of_birth: verificationForm.dateOfBirth, passport_id: verificationForm.passportId,
        nationality: verificationForm.nationality, document_date: format(verificationForm.documentDate, 'dd.MM.yyyy')
      };
      const res = await fetch(`${API}/api/verifications/generate`, { method: 'POST', headers: getHeaders(), body: JSON.stringify(body) });
      if (!res.ok) throw new Error((await res.json()).detail || 'Error');
      const data = await res.json();
      setVerifications(prev => [data, ...prev]);
      toast.success('Верификация создана');
    } catch (e) { toast.error(e.message); }
    setIsGeneratingVerification(false);
  };

  const generateEmployment = async () => {
    setIsGeneratingEmployment(true);
    try {
      const body = {
        template_id: selectedEmploymentTemplateId, full_name: employmentForm.fullName,
        passport_number: employmentForm.passportNumber, date_of_birth: employmentForm.dateOfBirth,
        country: employmentForm.country, document_date: format(employmentForm.documentDate, 'dd.MM.yyyy')
      };
      const res = await fetch(`${API}/api/employment-contracts/generate`, { method: 'POST', headers: getHeaders(), body: JSON.stringify(body) });
      if (!res.ok) throw new Error((await res.json()).detail || 'Error');
      const data = await res.json();
      setEmploymentContracts(prev => [data, ...prev]);
      toast.success('Трудовой договор создан');
    } catch (e) { toast.error(e.message); }
    setIsGeneratingEmployment(false);
  };

  const generateInvitation = async () => {
    setIsGeneratingInvitation(true);
    try {
      const body = {
        template_id: selectedInvitationTemplateId,
        visa_template_id: (selectedVisaTemplateId && selectedVisaTemplateId !== 'none') ? selectedVisaTemplateId : null,
        sponsor_name: invitationForm.sponsorName, certificate_number: invitationForm.certificateNumber,
        document_date: format(invitationForm.documentDate, 'dd.MM.yyyy'),
        arrival_date: format(invitationForm.arrivalDate, 'dd.MM.yyyy'),
        work_end_date: format(invitationForm.workEndDate, 'dd.MM.yyyy'),
        family_name: invitationForm.familyName, given_name: invitationForm.givenName,
        nationality: invitationForm.nationality, place_of_birth: invitationForm.placeOfBirth,
        date_of_birth: invitationForm.dateOfBirth, gender: invitationForm.gender,
        passport_number: invitationForm.passportNumber, passport_issue_date: invitationForm.passportIssueDate,
        passport_expiry_date: invitationForm.passportExpiryDate, passport_place_of_issue: invitationForm.passportPlaceOfIssue,
        employee_number: invitationForm.employeeNumber, job_title: invitationForm.jobTitle,
        salary: invitationForm.salary, visa_type: invitationForm.visaType,
        work_address: invitationForm.workAddress, work_city: invitationForm.workCity,
        work_county: invitationForm.workCounty, work_postcode: invitationForm.workPostcode,
        home_address: invitationForm.homeAddress, home_city: invitationForm.homeCity,
        home_postcode: invitationForm.homePostcode, home_country: invitationForm.homeCountry,
        contact_email: invitationForm.contactEmail
      };
      const res = await fetch(`${API}/api/invitations/generate`, { method: 'POST', headers: getHeaders(), body: JSON.stringify(body) });
      if (!res.ok) throw new Error((await res.json()).detail || 'Error');
      const data = await res.json();
      setInvitations(prev => [data, ...prev]);
      if (data.visa) { setVisas(prev => [data.visa, ...prev]); }
      toast.success(data.visa ? 'Приглашение и виза созданы' : 'Приглашение создано');
    } catch (e) { toast.error(e.message); }
    setIsGeneratingInvitation(false);
  };

  const generateAll = async () => {
    setIsGeneratingAll(true);
    const results = [];
    try {
      if (allForm.verificationTemplateId) {
        const body = {
          template_id: allForm.verificationTemplateId, full_name: allForm.fullName,
          date_of_birth: allForm.dateOfBirth, passport_id: allForm.passportId,
          nationality: allForm.nationality, document_date: format(allForm.verificationDate, 'dd.MM.yyyy')
        };
        const res = await fetch(`${API}/api/verifications/generate`, { method: 'POST', headers: getHeaders(), body: JSON.stringify(body) });
        if (res.ok) { const d = await res.json(); setVerifications(p => [d, ...p]); results.push('Верификация'); }
      }
      if (allForm.contractTemplateId) {
        const body = {
          template_id: allForm.contractTemplateId, full_name: allForm.fullName,
          passport_id: allForm.passportId, address: allForm.nationality,
          document_date: format(allForm.contractDate, 'dd.MM.yyyy')
        };
        const res = await fetch(`${API}/api/contracts/generate`, { method: 'POST', headers: getHeaders(), body: JSON.stringify(body) });
        if (res.ok) { const d = await res.json(); setContracts(p => [d, ...p]); results.push('Контракт'); }
      }
      if (allForm.employmentTemplateId) {
        const body = {
          template_id: allForm.employmentTemplateId, full_name: allForm.fullName,
          passport_number: allForm.passportId, date_of_birth: allForm.dateOfBirth,
          country: allForm.nationality, document_date: format(allForm.employmentDate, 'dd.MM.yyyy')
        };
        const res = await fetch(`${API}/api/employment-contracts/generate`, { method: 'POST', headers: getHeaders(), body: JSON.stringify(body) });
        if (res.ok) { const d = await res.json(); setEmploymentContracts(p => [d, ...p]); results.push('Трудовой'); }
      }
      if (results.length > 0) toast.success(`Созданы: ${results.join(', ')}`);
      else toast.error('Ошибка создания документов');
    } catch (e) { toast.error(e.message); }
    setIsGeneratingAll(false);
  };

  const generateStatement = async () => {
    setIsGeneratingStatement(true);
    try {
      const txs = statementForm.transactions.map(tx => ({
        date: tx.date,
        description: tx.description === '__custom__' ? (tx.customDescription || 'CUSTOM') : tx.description,
        type: tx.type,
        money_in: parseFloat(tx.money_in) || 0,
        money_out: parseFloat(tx.money_out) || 0
      }));
      const body = {
        template_id: selectedStatementTemplateId,
        surname: statementForm.surname,
        document_date: format(statementForm.documentDate, 'dd.MM.yyyy'),
        address_last_updated: format(statementForm.addressLastUpdated, 'dd.MM.yyyy'),
        sort_code: statementForm.sortCode,
        account_number: statementForm.accountNumber,
        statement_from: format(statementForm.statementFrom, 'dd.MM.yyyy'),
        statement_to: format(statementForm.statementTo, 'dd.MM.yyyy'),
        opening_balance: parseFloat(statementForm.openingBalance) || 0,
        transactions: txs
      };
      const res = await fetch(`${API}/api/statements/generate`, { method: 'POST', headers: getHeaders(), body: JSON.stringify(body) });
      if (!res.ok) throw new Error((await res.json()).detail || 'Error');
      const data = await res.json();
      setStatements(prev => [data, ...prev]);
      toast.success('Выписка создана');
    } catch (e) { toast.error(e.message); }
    setIsGeneratingStatement(false);
  };

  const isAdmin = user?.role === 'admin';

  if (!user) return null;

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-[#FAFAFA]" data-testid="dashboard">
      <header className="h-14 border-b border-[#262626] bg-[#0A0A0A]/95 backdrop-blur-sm flex items-center justify-between px-6 sticky top-0 z-50">
        <div className="flex items-center gap-2">
          <div className="w-6 h-6 bg-[#FACC15] flex items-center justify-center"><FileText size={14} weight="bold" className="text-[#0A0A0A]" /></div>
          <span className="font-semibold text-sm tracking-wider">DOCFLOW</span>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-xs text-[#A3A3A3]">{user.email}</span>
          <span className="text-xs bg-[#262626] px-2 py-0.5 text-[#A3A3A3]">{user.role}</span>
          {isAdmin && (
            <button onClick={() => navigate('/settings')} className="p-1.5 hover:bg-[#262626] transition-colors" data-testid="settings-btn">
              <GearSix size={16} className="text-[#A3A3A3]" />
            </button>
          )}
          <button onClick={logout} className="p-1.5 hover:bg-[#262626] transition-colors" data-testid="logout-btn">
            <SignOut size={16} className="text-[#A3A3A3]" />
          </button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto p-6">
        <Tabs defaultValue="contract" className="space-y-6">
          <TabsList className="bg-[#141414] border border-[#262626] rounded-none h-10 p-0.5" data-testid="dashboard-tabs">
            <TabsTrigger value="all" className="rounded-none h-full text-xs font-semibold uppercase tracking-[0.15em] data-[state=active]:bg-[#FACC15] data-[state=active]:text-[#0A0A0A] text-[#A3A3A3]" data-testid="tab-all">
              <Stack size={14} className="mr-1.5" />Все
            </TabsTrigger>
            <TabsTrigger value="contract" className="rounded-none h-full text-xs font-semibold uppercase tracking-[0.15em] data-[state=active]:bg-[#FACC15] data-[state=active]:text-[#0A0A0A] text-[#A3A3A3]" data-testid="tab-contract">
              <FileText size={14} className="mr-1.5" />Контракт
            </TabsTrigger>
            <TabsTrigger value="verification" className="rounded-none h-full text-xs font-semibold uppercase tracking-[0.15em] data-[state=active]:bg-[#FACC15] data-[state=active]:text-[#0A0A0A] text-[#A3A3A3]" data-testid="tab-verification">
              <ShieldCheck size={14} className="mr-1.5" />Верификация
            </TabsTrigger>
            <TabsTrigger value="employment" className="rounded-none h-full text-xs font-semibold uppercase tracking-[0.15em] data-[state=active]:bg-[#FACC15] data-[state=active]:text-[#0A0A0A] text-[#A3A3A3]" data-testid="tab-employment">
              <Certificate size={14} className="mr-1.5" />Трудовой
            </TabsTrigger>
            <TabsTrigger value="invitation" className="rounded-none h-full text-xs font-semibold uppercase tracking-[0.15em] data-[state=active]:bg-[#FACC15] data-[state=active]:text-[#0A0A0A] text-[#A3A3A3]" data-testid="tab-invitation">
              <AirplaneTilt size={14} className="mr-1.5" />Приглашение / Виза
            </TabsTrigger>
            <TabsTrigger value="statement" className="rounded-none h-full text-xs font-semibold uppercase tracking-[0.15em] data-[state=active]:bg-[#FACC15] data-[state=active]:text-[#0A0A0A] text-[#A3A3A3]" data-testid="tab-statement">
              <Bank size={14} className="mr-1.5" />Счёт
            </TabsTrigger>
          </TabsList>

          <TabsContent value="all">
            <AllTab allForm={allForm} setAllForm={setAllForm} isAdmin={isAdmin} isGeneratingAll={isGeneratingAll}
              verificationTemplates={verificationTemplates} contractTemplates={contractTemplates}
              employmentTemplates={employmentTemplates} onGenerateAll={generateAll} />
          </TabsContent>
          <TabsContent value="contract">
            <ContractTab contractTemplates={contractTemplates} selectedContractTemplateId={selectedContractTemplateId}
              setSelectedContractTemplateId={setSelectedContractTemplateId} contractForm={contractForm}
              setContractForm={setContractForm} isGenerating={isGenerating} isAdmin={isAdmin}
              contracts={contracts} loadingContracts={loadingContracts}
              onGenerate={generateContract}
              onDownload={(id, name) => handleDownload(`/api/contracts/${id}/download`, `Contract_${name}.pdf`)}
              onDelete={(id) => handleDelete(`/api/contracts/${id}`, id, setContracts)} />
          </TabsContent>
          <TabsContent value="verification">
            <VerificationTab verificationTemplates={verificationTemplates} selectedVerificationTemplateId={selectedVerificationTemplateId}
              setSelectedVerificationTemplateId={setSelectedVerificationTemplateId} verificationForm={verificationForm}
              setVerificationForm={setVerificationForm} isGeneratingVerification={isGeneratingVerification} isAdmin={isAdmin}
              verifications={verifications} loadingVerifications={loadingVerifications}
              onGenerate={generateVerification}
              onDownload={(id, name) => handleDownload(`/api/verifications/${id}/download`, `Verification_${name}.pdf`)}
              onDelete={(id) => handleDelete(`/api/verifications/${id}`, id, setVerifications)} />
          </TabsContent>
          <TabsContent value="employment">
            <EmploymentTab employmentTemplates={employmentTemplates} selectedEmploymentTemplateId={selectedEmploymentTemplateId}
              setSelectedEmploymentTemplateId={setSelectedEmploymentTemplateId} employmentForm={employmentForm}
              setEmploymentForm={setEmploymentForm} isGeneratingEmployment={isGeneratingEmployment} isAdmin={isAdmin}
              employmentContracts={employmentContracts} loadingEmployment={loadingEmployment}
              onGenerate={generateEmployment}
              onDownload={(id, name) => handleDownload(`/api/employment-contracts/${id}/download`, `Employment_${name}.pdf`)}
              onDelete={(id) => handleDelete(`/api/employment-contracts/${id}`, id, setEmploymentContracts)} />
          </TabsContent>
          <TabsContent value="invitation">
            <InvitationVisaTab invitationTemplates={invitationTemplates} selectedInvitationTemplateId={selectedInvitationTemplateId}
              setSelectedInvitationTemplateId={setSelectedInvitationTemplateId} visaTemplates={visaTemplates}
              selectedVisaTemplateId={selectedVisaTemplateId} setSelectedVisaTemplateId={setSelectedVisaTemplateId}
              invitationForm={invitationForm} setInvitationForm={setInvitationForm}
              isGeneratingInvitation={isGeneratingInvitation} isAdmin={isAdmin}
              invitations={invitations} loadingInvitations={loadingInvitations} visas={visas}
              onGenerate={generateInvitation}
              onDownloadInvitation={(id, name) => handleDownload(`/api/invitations/${id}/download`, `Invitation_${name}.pdf`)}
              onDeleteInvitation={(id) => handleDelete(`/api/invitations/${id}`, id, setInvitations)}
              onDownloadVisa={(id, name) => handleDownload(`/api/visas/${id}/download`, `Visa_${name}.pdf`)}
              onDeleteVisa={(id) => handleDelete(`/api/visas/${id}`, id, setVisas)} />
          </TabsContent>
          <TabsContent value="statement">
            <StatementTab statementTemplates={statementTemplates} selectedStatementTemplateId={selectedStatementTemplateId}
              setSelectedStatementTemplateId={setSelectedStatementTemplateId} statementForm={statementForm}
              setStatementForm={setStatementForm} isGeneratingStatement={isGeneratingStatement} isAdmin={isAdmin}
              statements={statements} loadingStatements={loadingStatements}
              onGenerate={generateStatement}
              onDownload={(id, name) => handleDownload(`/api/statements/${id}/download`, `Statement_${name}.pdf`)}
              onDelete={(id) => handleDelete(`/api/statements/${id}`, id, setStatements)} />
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
};

export { Dashboard };
export default Dashboard;
